import type { ReactNode } from 'react';
import { useFormat, type Formatter } from '@paige/format';
import { KpiRow, type Kpi } from '../insights/KpiTile';
import type { CustomerFilterSet, CustomerSummary } from './customer-types';
import { changeOf } from './customer-words';
import './customers.css';

export interface CustomerTilesProps {
  summary: CustomerSummary;
  filters: CustomerFilterSet;
  /** Names a figure with its period: "Paying, last 30 days". */
  label: (what: string) => string;
  /** Pressing Paying, Past due or With problems switches that filter. */
  onToggle: (filter: 'paying' | 'pastDue' | 'problems') => void;
}

/** The three counts that are switches: each says its number and turns its own filter on or off. */
function switches(props: CustomerTilesProps, f: Formatter): Kpi[] {
  const { summary: s, filters, label, onToggle } = props;
  const paying = changeOf(s.paying, s.previousPaying, (n) => f.int(n));
  return [
    {
      label: label('Paying'),
      value: f.int(s.paying),
      change: paying.arrow,
      tone: paying.tone,
      foot: paying.text,
      onPress: () => onToggle('paying'),
      pressed: filters.paying,
    },
    {
      label: 'Past due, now',
      value: f.int(s.pastDue),
      foot: 'a card that stopped paying',
      danger: s.pastDue > 0,
      onPress: () => onToggle('pastDue'),
      pressed: filters.pastDue,
    },
    {
      label: 'With problems, now',
      value: f.int(s.withProblems),
      foot: 'stopped work, failed exports, held for pages',
      danger: s.withProblems > 0,
      onPress: () => onToggle('problems'),
      pressed: filters.problems,
    },
  ];
}

/**
 * The four counts over the companies the filters leave, worked out by the ledger. Three of them are
 * switches for their own filter, so "how many are past due" and "show me them" are one press.
 */
export function CustomerTiles(props: CustomerTilesProps): ReactNode {
  const f = useFormat();
  const s = props.summary;
  const companies: Kpi = {
    label: 'Companies',
    value: f.int(s.companies),
    foot: s.tests > 0 ? `${f.int(s.tests)} of them test companies` : 'in these filters',
  };
  return <KpiRow kpis={[companies, ...switches(props, f)]} label="Customers in numbers" />;
}
